import {variantClasses} from './responsive';

/**
 * Semantic accent roles, named the way `TypographyColor` names them — a component says what a colour
 * *means* and the design system decides which token that is, so `success` can never be tone-green in
 * one component and something else in the next. The ACCENT_* maps below are where that decision is
 * made; a component picks the map matching the property it paints and never names a token itself.
 *
 * A const array rather than a bare union because Tag and Card both need the list at runtime, as do
 * their tests, stories and the DesignSystem gallery.
 */
export const ACCENT_COLORS = ['primary', 'critical', 'medium', 'low', 'success', 'info'] as const;

export type AccentColor = typeof ACCENT_COLORS[number];

export const ACCENT_TEXT: Record<AccentColor, string> = {
	primary: 'text-brand-main',
	critical: 'text-tone-red',
	medium: 'text-tone-orange',
	low: 'text-tone-yellow',
	success: 'text-tone-green',
	info: 'text-tone-teal'
};

export const ACCENT_FILL: Record<AccentColor, string> = {
	primary: 'border-brand-main bg-brand-main/20',
	critical: 'border-tone-red bg-tone-red/20',
	medium: 'border-tone-orange bg-tone-orange/20',
	low: 'border-tone-yellow bg-tone-yellow/20',
	success: 'border-tone-green bg-tone-green/20',
	info: 'border-tone-teal bg-tone-teal/20'
};

export const ACCENT_RAIL: Record<AccentColor, string> = {
	primary: 'border-l-brand-main',
	critical: 'border-l-tone-red',
	medium: 'border-l-tone-orange',
	low: 'border-l-tone-yellow',
	success: 'border-l-tone-green',
	info: 'border-l-tone-teal'
};

/**
 * The v3 palette, as Tailwind names it — see the `colors` block in tailwind.config.js. Raw tokens, not
 * roles: this is for props that paint a literal surface (Stack's backgroundColor), where the caller
 * has already decided which colour it wants.
 */
export type PaletteColor =
	'foreground-contrast' | 'foreground-white' | 'foreground-red' |
	'foreground-100' | 'foreground-200' | 'foreground-300' | 'foreground-400' |
	'background-500' | 'background-600' | 'background-700' | 'background-800' | 'background-1000' |
	'brand-main' | 'brand-main-light' | 'brand-main-dark' |
	'tone-red' | 'tone-orange' | 'tone-yellow' | 'tone-green' |
	'tone-indigo' | 'tone-purple' | 'tone-teal' | 'tone-light-blue';

export type ColorState = 'default' | 'hover' | 'active' | 'focus';

export type StatefulValue<T> = T | Partial<Record<ColorState, T>>;

/**
 * `responsive`, keyed by interaction state instead of width — same scalar-or-object shape, and the
 * same safelist caveat, since these classes are built at runtime too. `focus` emits
 * `focus-visible:`: the kit styles keyboard focus only, never a mouse click's lingering ring.
 */
export function stateful<T extends string>(
		value: StatefulValue<T> | undefined,
		toClass: (value: T) => string
): string {
	return variantClasses(value, STATE_PREFIX, toClass);
}

// Base first, then states, so a later state beats the base at equal specificity.
const STATE_PREFIX: Record<ColorState, string> = {
	default: '',
	hover: 'hover:',
	active: 'active:',
	focus: 'focus-visible:'
};
